import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  FaBook,
  FaBookOpen,
  FaQuran,
  FaGraduationCap,
  FaCertificate,
  FaLanguage,
  FaPrayingHands,
  FaMosque,
  FaStar,
  FaChevronRight,
  FaGift,
  FaUsers,
  FaClock,
  FaChevronDown
} from 'react-icons/fa';
import { coursesData } from '../data/coursesData';
import { menuData } from '../data/menuData';
import './Courses.css';

// Map URL hash (group) to course categories for filtering
const HASH_TO_CATEGORIES = {
  qaida: ['qaida'],
  'quran-reading': ['reading', 'memorization'],
  'quran-recitation': ['recitation', 'tajweed', 'ijazah'],
  'quran-translation': ['tafseer'],
  'islamic-studies': ['islamic-studies'],
  hadith: ['hadith'],
  'arabic-speaking': ['arabic']
};

const coursesMenuItems = menuData.find((m) => m.path === '/courses')?.submenu ?? [];

const Courses = () => {
  const sectionRef = useRef(null);
  const { hash } = useLocation();
  const hashKey = hash ? hash.replace('#', '') : '';
  const [isVisible, setIsVisible] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSidebarItems, setExpandedSidebarItems] = useState(() =>
    hashKey ? [hashKey] : []
  );

  // Sync filter from URL hash
  useEffect(() => {
    if (!hashKey) {
      setSelectedCategory('all');
      return;
    }
    if (HASH_TO_CATEGORIES[hashKey]) {
      setSelectedCategory(hashKey);
    }
    setExpandedSidebarItems((prev) =>
      prev.includes(hashKey) ? prev : [...prev, hashKey]
    );
  }, [hashKey]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const toggleSidebarItem = (path) => {
    const key = path.replace('/courses#', '');
    if (!key) return;
    setExpandedSidebarItems((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  // Map course titles to appropriate icons
  const getCourseIcon = (title) => {
    const titleLower = title.toLowerCase();
    
    if (titleLower.includes('reading') || titleLower.includes('qaida') || titleLower.includes('noorani') || titleLower.includes('madani')) {
      return <FaBookOpen />;
    }
    if (titleLower.includes('tajweed') || titleLower.includes('recitation')) {
      return <FaQuran />;
    }
    if (titleLower.includes('memorization') || titleLower.includes('hifz') || titleLower.includes('hafiz')) {
      return <FaGraduationCap />;
    }
    if (titleLower.includes('ijazah') || titleLower.includes('certificate')) {
      return <FaCertificate />;
    }
    if (titleLower.includes('arabic') || titleLower.includes('language')) {
      return <FaLanguage />;
    }
    if (titleLower.includes('qirat') || titleLower.includes('ten qirat')) {
      return <FaStar />;
    }
    if (titleLower.includes('dua') || titleLower.includes('supplication') || titleLower.includes('namaz')) {
      return <FaPrayingHands />;
    }
    if (titleLower.includes('islamic studies') || titleLower.includes('tafseer') || titleLower.includes('hadith')) {
      return <FaMosque />;
    }
    // Default icon
    return <FaBook />;
  };

  // Get unique categories (for button strip)
  const categories = ['all', ...new Set(coursesData.map((course) => course.category))];

  // Filter: by group (hash) or by single category
  const categoryList = HASH_TO_CATEGORIES[selectedCategory];
  const filteredCourses =
    selectedCategory === 'all'
      ? coursesData
      : categoryList
        ? coursesData.filter((course) => categoryList.includes(course.category))
        : coursesData.filter((course) => course.category === selectedCategory);

  return (
    <div className="courses-page">
      {/* Hero Header */}
      <div className="courses-hero">
        <div className="courses-hero-overlay"></div>
        <div className="container">
          <div className="courses-hero-content">
            <div className="courses-hero-icon-wrapper">
              <FaBook className="courses-hero-icon" />
            </div>
            <span className="courses-hero-badge">Our Programs</span>
            <h1 className="courses-hero-title">Our Courses</h1>
            <p className="courses-hero-subtitle">
              Choose from our comprehensive range of Quran learning courses designed for all levels
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <section ref={sectionRef} className={`courses-content-section ${isVisible ? 'visible' : ''}`}>
        <div className="container courses-layout">
          {/* Courses section: left sidebar (highlighted area) */}
          <section className="courses-nav-section" aria-label="Course categories">
            <aside className="courses-sidebar">
              <div className="courses-sidebar-inner">
              <h3 className="courses-sidebar-title">Courses</h3>
              <nav className="courses-sidebar-nav">
                {coursesMenuItems.map((item) => {
                  const isParent = item.submenu && item.submenu.length > 0;
                  const itemHash = item.path.includes('#') ? item.path.replace('/courses#', '') : null;
                  const isExpanded = itemHash ? expandedSidebarItems.includes(itemHash) : false;
                  const isActive = itemHash && selectedCategory === itemHash;

                  if (isParent) {
                    return (
                      <div key={item.path} className="courses-sidebar-group">
                        <Link
                          to={item.path}
                          className={`courses-sidebar-link ${isActive ? 'active' : ''}`}
                          onClick={(e) => {
                            if (hashKey === itemHash) {
                              e.preventDefault();
                              toggleSidebarItem(item.path);
                            }
                          }}
                        >
                          <span>{item.title}</span>
                          <FaChevronDown
                            className={`sidebar-chevron ${isExpanded ? 'expanded' : ''}`}
                          />
                        </Link>
                        <ul className={`courses-sidebar-sublist ${isExpanded ? 'open' : ''}`}>
                          {item.submenu.map((sub) => (
                            <li key={sub.path}>
                              <Link to={sub.path}>{sub.title}</Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  }
                  if (item.path === '/courses') {
                    return (
                      <Link
                        key={item.path}
                        to="/courses"
                        className={`courses-sidebar-link ${selectedCategory === 'all' ? 'active' : ''}`}
                        onClick={() => setSelectedCategory('all')}
                      >
                        {item.title}
                      </Link>
                    );
                  }
                  return (
                    <Link key={item.path} to={item.path} className="courses-sidebar-link">
                      {item.title}
                    </Link>
                  );
                })}
              </nav>
              </div>
            </aside>
          </section>

          <div className="courses-main">
            {/* Category Filter */}
            <div className="courses-filter">
              <div className="filter-label">Filter by Category:</div>
              <div className="filter-buttons">
                <button
                  className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('all')}
                >
                  All Courses
                </button>
                {categories.filter((c) => c !== 'all').map((category) => (
                  <button
                    key={category}
                    className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category.charAt(0).toUpperCase() + category.slice(1).replace(/-/g, ' ')}
                  </button>
                ))}
              </div>
            </div>

            {/* Courses Grid */}
          <div className="courses-grid">
            {filteredCourses.map((course, index) => {
              const Icon = getCourseIcon(course.title);
              return (
                <div
                  key={course.id}
                  className={`course-card-new ${isVisible ? 'visible' : ''}`}
                  style={{ animationDelay: `${(index % 6) * 0.1}s` }}
                >
                  <Link to={`/courses/${course.slug}`} className="course-card-link">
                    <div className="course-icon-wrapper">
                      <div className="course-icon-bg">
                        <div className="course-icon">
                          {Icon}
                        </div>
                      </div>
                      {course.badge && (
                        <div className="course-badge-new">
                          {course.badge}
                        </div>
                      )}
                    </div>
                    
                    <div className="course-content-new">
                      <h3 className="course-title-new">{course.title}</h3>
                      <p className="course-description-new">{course.description}</p>
                      
                      <div className="course-meta-new">
                        {course.duration && (
                          <div className="course-meta-item">
                            <FaClock className="meta-icon" />
                            <span>{course.duration}</span>
                          </div>
                        )}
                        {course.level && (
                          <div className="course-meta-item">
                            <FaGraduationCap className="meta-icon" />
                            <span>{course.level}</span>
                          </div>
                        )}
                        {course.students && (
                          <div className="course-meta-item">
                            <FaUsers className="meta-icon" />
                            <span>{course.students} Students</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="course-footer-new">
                      <span className="course-link-text">
                        View Course Details
                        <FaChevronRight className="link-arrow" />
                      </span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>

            {/* CTA Section */}
            <div className="courses-cta">
              <div className="courses-cta-icon">
                <FaGift />
              </div>
              <h3>Ready to Start Learning?</h3>
              <p>Join thousands of students learning Quran online with expert tutors. Start your journey with a free trial today!</p>
              <Link to="/free-trial" className="courses-cta-btn">
                <span>Book Free Trial</span>
                <FaGift className="btn-icon" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Courses;
